import { types, flow, Instance } from 'mobx-state-tree';
import * as testService from '@src/services/tests';

const AnswerModel = types.model('Answer', {
  id: types.number,
  text: types.string,
});

const QuestionModel = types.model('Question', {
  id: types.number,
  text: types.string,
  answers: types.array(AnswerModel),
});

const ExampleQuestionModel = types.model('ExampleQuestion', {
  id: types.number,
  text: types.string,
  answers: types.array(AnswerModel),
});

const CompanyModel = types.model('Company', {
  name: types.string,
  logo_url: types.maybeNull(types.string),
  cover_url: types.maybeNull(types.string),
  color: types.maybeNull(types.string),
});

const JobModel = types.model('Job', {
  position_title: types.string,
});

const ProgressModel = types.model('Progress', {
  answered: types.number,
  total: types.number,
  percentage: types.number,
});

const CustomTestDocumentModel = types.model('CustomTestDocument', {
  id: types.number,
  name: types.string,
  file_url: types.string,
  file_type: types.string,
});

export const TestStore = types
  .model('TestStore', {
    testId: types.maybeNull(types.number),
    applicationId: types.maybeNull(types.number),
    state: types.maybeNull(types.string),
    title: types.maybeNull(types.string),
    description: types.maybeNull(types.string),
    timeLimit: types.maybeNull(types.number),
    timeLeft: types.maybeNull(types.number),
    coverImg: types.maybeNull(types.string),
    exampleQuestions: types.array(ExampleQuestionModel),
    company: types.maybeNull(CompanyModel),
    job: types.maybeNull(JobModel),
    currentQuestion: types.maybeNull(QuestionModel),
    progress: types.maybeNull(ProgressModel),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
    testStarted: types.optional(types.boolean, false),
    testCompleted: types.optional(types.boolean, false),
    finishDate: types.maybeNull(types.string),
    finalScore: types.maybeNull(types.number),
    testType: types.maybeNull(types.string),
    customTestDocs: types.array(CustomTestDocumentModel),
    lastAnswer: types.maybeNull(
      types.model({
        isCorrect: types.boolean,
        correctAnswer: types.string,
      }),
    ),
  })
  .actions((self) => ({
    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    setError(error: string | null) {
      self.error = error;
    },
    setTestDetails(details: testService.ITestDetails) {
      self.testId = details.id;
      self.applicationId = details.application_id;
      self.state = details.state;
      self.title = details.title;
      self.description = details.description;
      self.timeLimit = details.time_limit;
      self.timeLeft = details.time_left;
      self.exampleQuestions.replace(details.example_questions as any);
      self.company = details.company as any;
      self.job = details.job as any;
      self.testType = details.test_type || null;
      console.log('Setting customTestDocs from details:', details.custom_test_docs);
      self.customTestDocs.replace((details.custom_test_docs || []) as any);

      if (details.state === 'finished' || details.state === 'review') {
        self.testCompleted = true;
      } else if (details.state === 'in_progress') {
        self.testStarted = true;
      }
    },
    setCurrentQuestion(question: testService.IQuestion | null) {
      self.currentQuestion = question as any;
    },
    setProgress(progress: testService.ITestProgress) {
      self.progress = progress as any;
    },
    setTimeLeft(timeLeft: number) {
      self.timeLeft = timeLeft;
    },
    setTestStarted(started: boolean) {
      self.testStarted = started;
    },
    setTestCompleted(completed: boolean) {
      self.testCompleted = completed;
    },
    setFinishDate(finishDate: string | null) {
      self.finishDate = finishDate;
    },
    setFinalScore(score: number) {
      self.finalScore = score;
    },
    setLastAnswer(isCorrect: boolean, correctAnswer: string) {
      self.lastAnswer = { isCorrect, correctAnswer } as any;
    },
    clearLastAnswer() {
      self.lastAnswer = null;
    },
  }))
  .actions((self) => ({
    loadTestDetails: flow(function* (testId: number) {
      self.setLoading(true);
      self.setError(null);

      // The store is a global singleton that survives SPA navigation / HMR.
      // Reset per-test transient state so a previous attempt (testCompleted,
      // testStarted, a stale finish_date, etc.) cannot leak into this test and
      // make it appear already finished.
      self.testStarted = false;
      self.testCompleted = false;
      self.finishDate = null;
      self.finalScore = null;
      self.timeLeft = null;
      self.currentQuestion = null;
      self.lastAnswer = null;

      try {
        const details: testService.ITestDetails = yield testService.getTestDetails(testId);
        self.setTestDetails(details);
      } catch (error: any) {
        self.setError(error.message || 'Failed to load test details');
      } finally {
        self.setLoading(false);
      }
    }),

    startTest: flow(function* () {
      if (!self.testId) {
        self.setError('Test ID is not set');
        return;
      }

      self.setLoading(true);
      self.setError(null);

      try {
        const response: testService.IStartTestResponse = yield testService.startTest(self.testId);

        console.log(response);

        if (response.status === 'ok') {
          console.log('Setting customTestDocs from start response:', response.custom_test_docs);
          self.customTestDocs.replace(response.custom_test_docs || []);
          self.setCurrentQuestion(response.question);
          self.setTimeLeft(response.time_left);
          self.setProgress(response.progress);
          self.setTestStarted(true);
          self.setFinishDate(response.finish_date);
        }
      } catch (error: any) {
        self.setError(error.message || 'Failed to start test');
      } finally {
        self.setLoading(false);
      }
    }),

    submitAnswer: flow(function* (answer: string) {
      if (!self.testId || !self.currentQuestion) {
        self.setError('Test or question not available');
        return;
      }

      self.setLoading(true);
      self.setError(null);
      self.clearLastAnswer();

      try {
        const response: testService.IAnswerResponse = yield testService.submitAnswer(
          self.testId,
          self.currentQuestion.id,
          answer,
        );

        // Store feedback about the answer
        self.setLastAnswer(response.is_correct, response.correct_answer);

        if (response.status === 'completed') {
          // Test is finished
          self.setTestCompleted(true);
          self.setFinalScore(response.final_score || 0);
          self.setCurrentQuestion(null);
        } else if (response.status === 'ok' && response.next_question) {
          // Move to next question
          self.setCurrentQuestion(response.next_question);
          if (response.time_left !== undefined) {
            self.setTimeLeft(response.time_left);
          }
          if (response.progress) {
            self.setProgress(response.progress);
          }
        }
      } catch (error: any) {
        self.setError(error.message || 'Failed to submit answer');
      } finally {
        self.setLoading(false);
      }
    }),

    finishTest: flow(function* () {
      if (!self.testId) {
        self.setError('Test ID is not set');
        return;
      }

      self.setLoading(true);
      self.setError(null);

      try {
        const response: testService.IFinishTestResponse = yield testService.finishTest(self.testId);

        self.setTestCompleted(true);
        // self.state = 'finished'; // Update state to finished
        if (response.state) {
          self.state = response.state;
        }
        self.setFinalScore(response.final_score);
        self.setCurrentQuestion(null);
      } catch (error: any) {
        self.setError(error.message || 'Failed to finish test');
      } finally {
        self.setLoading(false);
      }
    }),

    submitFiles: flow(function* (files: File[]) {
      if (!self.testId) {
        self.setError('Test ID is not set');
        return;
      }

      self.setLoading(true);
      self.setError(null);

      try {
        const response = yield testService.uploadTestFiles(self.testId, files);
        console.log('Upload result:', response);
        self.setTestCompleted(true);
        // self.state = 'finished';
        if (response.state) {
          self.state = response.state;
        }
        return response;
      } catch (error: any) {
        self.setError(error.message || 'Failed to upload files');
        throw error;
      } finally {
        self.setLoading(false);
      }
    }),

    validateTest: flow(function* (
      companyId: string | number,
      jobId: string | number,
      applicantId: string | number,
      testId: string | number,
      score: number,
    ) {
      self.setLoading(true);
      self.setError(null);

      try {
        yield testService.validateTest(companyId, jobId, applicantId, testId, score);
        self.state = 'finished';
        self.setFinalScore(score);
        self.setTestCompleted(true);
      } catch (error: any) {
        self.setError(error.message || 'Failed to validate test');
        throw error;
      } finally {
        self.setLoading(false);
      }
    }),

    manualUploadTest: flow(function* (
      companyId: string | number,
      jobId: string | number,
      applicantId: string | number,
      testId: string | number,
      file: File,
    ) {
      self.setLoading(true);
      self.setError(null);

      try {
        yield testService.manualUploadTestFile(companyId, jobId, applicantId, testId, file);
      } catch (error: any) {
        self.setError(error.message || 'Failed to upload file');
        throw error;
      } finally {
        self.setLoading(false);
      }
    }),

    resetTest() {
      self.currentQuestion = null;
      self.testStarted = false;
      self.testCompleted = false;
      self.finalScore = null;
      self.lastAnswer = null;
      self.error = null;
    },
  }));

export interface ITestStore extends Instance<typeof TestStore> {}
